import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePresenceCounts } from '@/api/queries';
import { useAppConfig, useIsPreprinted } from '@/app/providers/config';
import { Card, CardContent, CardHeader, CardTitle } from '@/design/components/card';
import { useSession } from '@/features/auth/session';
import { useFormatters } from '@/i18n/use-format';

/**
 * The first screen after signing in.
 *
 * It exists in this phase to make the configuration provider observable: the issuance mode and the
 * reporting timezone are the two values the whole application branches on, and a screen that shows
 * what this installation is actually running is the cheapest way for an operator to notice that a
 * terminal is pointed at the wrong one. The dashboard panels replace its body later; the three
 * facts stay.
 */
export function OverviewPage(): ReactNode {
  // **A ticking clock, and it is the TERMINAL's.** The card used to render `new Date()` under a
  // label that said "server time": nothing here fetches a server instant — `clientConfigSchema`
  // carries the mode, the locale and the timezone and no clock — so the value was this terminal's
  // own, formatted in the installation's zone, and frozen at whatever render last happened. On the
  // one screen a terminal sits on while nobody is using it, that is a figure claiming an authority
  // it does not have and stale by however long the tab has been open. It is now named for what it
  // is and it moves.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const { t } = useTranslation();
  const config = useAppConfig();
  const preprinted = useIsPreprinted();
  const session = useSession();
  const format = useFormatters();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('overview.title')}</h1>
        {session !== null && (
          <p className="text-sm text-muted-foreground">
            {t('overview.welcome', { name: session.user.displayName })}
          </p>
        )}
      </div>

      <PresenceCard />

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('overview.issuanceMode')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-medium" data-testid="issuance-mode">
              {t(preprinted ? 'overview.issuanceModePreprinted' : 'overview.issuanceModeOnDemand')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('overview.reportingTimezone')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-medium">{config.reportingTimezone}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('overview.terminalTime')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-medium" data-numeric data-testid="server-time">
              {format.dateTime(now)}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * The presence picture, on the screen a terminal sits on when nobody is using it.
 *
 * **Counts and no names, deliberately.** This screen is unattended for most of a shift, so a list of
 * the residents who are currently out would be a list of residents on a screen nobody is standing at.
 * What it says is how many are out and how many of those are past the allowance — enough for anybody
 * walking past to see that something needs attention. The list, with names, is an administration
 * screen, and the alert is what says to go and open it.
 *
 * **The overdue figure is never signalled by colour alone.** It is a labelled figure like the two
 * beside it, and it says how long the allowance is, so the number means something without a legend.
 */
function PresenceCard(): ReactNode {
  const { t } = useTranslation();
  const format = useFormatters();
  const { counts } = usePresenceCounts();

  return (
    <Card data-testid="presence-card">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {t('overview.presence')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {counts === null ? (
          <p className="text-sm text-muted-foreground">{t('overview.presenceLoading')}</p>
        ) : (
          <dl className="grid gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">{t('overview.presenceOutside')}</dt>
              <dd className="text-lg font-medium" data-numeric data-testid="overview-outside">
                {format.integer(counts.outside)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">
                {t('overview.presenceOverdue', {
                  minutes: format.integer(counts.overdueAfterMinutes),
                })}
              </dt>
              <dd className="text-lg font-medium" data-numeric data-testid="overview-overdue">
                {format.integer(counts.overdue)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{t('overview.presenceInside')}</dt>
              <dd className="text-lg font-medium" data-numeric data-testid="overview-inside">
                {format.integer(counts.inside)}
              </dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
